import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  useTheme,
  Skeleton,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Add as AddIcon,
  Description as ReportIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useReportsList, ReportListItem } from "../hooks/useReportsList";
import { useReportQuota } from "../hooks/useReportQuota";
import QuotaIndicator from "./QuotaIndicator";

dayjs.extend(advancedFormat);

interface ReportSidebarProps {
  ticker: string;
  selectedReportId: string | null;
  onSelectReport: (reportId: string) => void;
  onGenerateReport: () => void;
  isGenerating: boolean;
  generatingReportId?: string | null;
}

const EXPANDED_WIDTH = 200;
const COLLAPSED_WIDTH = 40;

const ReportSidebar: React.FC<ReportSidebarProps> = ({
  ticker,
  selectedReportId,
  onSelectReport,
  onGenerateReport,
  isGenerating,
  generatingReportId,
}) => {
  const theme = useTheme();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { data: reportsList, isLoading: isLoadingList } =
    useReportsList(ticker);
  const { data: quota } = useReportQuota();

  const reports = reportsList?.reports ?? [];
  // Past the free weekly quota, credits can still fund extra reports — only
  // block when both are gone (mirror of the server's paid path).
  const isQuotaExhausted = quota?.remaining === 0;
  const canPayWithCredits = (quota?.credits?.extraReportsAvailable ?? 0) > 0;
  const canGenerate = (!isQuotaExhausted || canPayWithCredits) && !isGenerating;

  // Format date for display
  const formatReportDate = (dateString: string) => {
    const date = dayjs(dateString);
    return {
      date: date.format("MMM D"),
      year: date.format("YYYY"),
      time: date.format("h:mm A"),
    };
  };

  // Collapsed view - minimal tab
  if (isCollapsed) {
    return (
      <Box
        sx={{
          width: COLLAPSED_WIDTH,
          minWidth: COLLAPSED_WIDTH,
          mb: { xs: 2, md: 3 },
          ml: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "fit-content",
          borderRadius: 2,
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(0, 0, 0, 0.015)",
          border:
            theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.08)"
              : "1px solid rgba(0, 0, 0, 0.08)",
          py: 1,
        }}
      >
        {/* Expand button */}
        <Tooltip title="Show reports panel" arrow placement="left">
          <IconButton
            onClick={() => setIsCollapsed(false)}
            size="small"
            sx={{
              width: 28,
              height: 28,
              color:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.5)"
                  : "rgba(0, 0, 0, 0.4)",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.06)",
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        {/* Reports icon with count badge */}
        <Tooltip
          title={`${reports.length} report${reports.length !== 1 ? "s" : ""}`}
          arrow
          placement="left"
        >
          <Box sx={{ mt: 1 }}>
            <Badge
              badgeContent={reports.length}
              color="primary"
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.6rem",
                  height: 16,
                  minWidth: 16,
                  backgroundColor: "#22c55e",
                  color: "white",
                },
              }}
            >
              <ReportIcon
                sx={{
                  fontSize: 20,
                  color:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.4)"
                      : "rgba(0, 0, 0, 0.35)",
                }}
              />
            </Badge>
          </Box>
        </Tooltip>

        {/* Generate button - compact */}
        <Tooltip
          title={
            isQuotaExhausted
              ? canPayWithCredits
                ? `Generate with ${quota?.credits?.reportPriceCredits ?? 25} credits`
                : "Weekly limit reached"
              : isGenerating
                ? "Generating..."
                : "Generate new report"
          }
          arrow
          placement="left"
        >
          <span>
            <IconButton
              onClick={onGenerateReport}
              disabled={!canGenerate}
              size="small"
              sx={{
                mt: 1.5,
                width: 26,
                height: 26,
                backgroundColor: canGenerate
                  ? "#22c55e"
                  : theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.04)",
                color: canGenerate
                  ? "white"
                  : theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.25)"
                    : "rgba(0, 0, 0, 0.25)",
                "&:hover": {
                  backgroundColor: canGenerate
                    ? "#16a34a"
                    : theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.06)",
                },
                "&:disabled": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.04)",
                  color:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.25)"
                      : "rgba(0, 0, 0, 0.25)",
                },
              }}
            >
              {isGenerating ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <AddIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  // Expanded view - full sidebar
  return (
    <Box
      sx={{
        width: EXPANDED_WIDTH,
        minWidth: EXPANDED_WIDTH,
        mb: { xs: 2, md: 3 },
        ml: 0,
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        maxHeight: { xs: "calc(100% - 32px)", md: "calc(100% - 48px)" },
        borderRadius: 2,
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.02)"
            : "rgba(0, 0, 0, 0.015)",
        border:
          theme.palette.mode === "dark"
            ? "1px solid rgba(255, 255, 255, 0.08)"
            : "1px solid rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
      }}
    >
      {/* Header - Title, Collapse, and Generate button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          py: 0.75,
          borderBottom:
            theme.palette.mode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.06)"
              : "1px solid rgba(0, 0, 0, 0.06)",
        }}
      >
        {/* Collapse button */}
        <Tooltip title="Hide panel" arrow placement="bottom">
          <IconButton
            onClick={() => setIsCollapsed(true)}
            size="small"
            sx={{
              width: 24,
              height: 24,
              color:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.4)"
                  : "rgba(0, 0, 0, 0.35)",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.08)"
                    : "rgba(0, 0, 0, 0.06)",
              },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(0, 0, 0, 0.45)",
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Reports
        </Typography>

        {/* Generate Button */}
        <Tooltip
          title={
            isQuotaExhausted
              ? canPayWithCredits
                ? `Generate with ${quota?.credits?.reportPriceCredits ?? 25} credits`
                : "Weekly limit reached"
              : isGenerating
                ? "Generating..."
                : "Generate new report"
          }
          arrow
          placement="left"
        >
          <span>
            <IconButton
              onClick={onGenerateReport}
              disabled={!canGenerate}
              size="small"
              sx={{
                width: 24,
                height: 24,
                backgroundColor: canGenerate
                  ? "#22c55e"
                  : theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.04)",
                color: canGenerate
                  ? "white"
                  : theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.25)"
                    : "rgba(0, 0, 0, 0.25)",
                "&:hover": {
                  backgroundColor: canGenerate
                    ? "#16a34a"
                    : theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.06)",
                },
                "&:disabled": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.04)",
                  color:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.25)"
                      : "rgba(0, 0, 0, 0.25)",
                },
              }}
            >
              {isGenerating ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <AddIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Reports List - max height for ~5 reports, then scroll */}
      <Box
        sx={{
          maxHeight: 240,
          overflowY: "auto",
          overflowX: "hidden",
          py: 0.5,
          "&::-webkit-scrollbar": {
            width: 4,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.15)"
                : "rgba(0, 0, 0, 0.1)",
            borderRadius: 2,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.25)"
                : "rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        {isLoadingList && (
          <Box sx={{ px: 1.5, py: 1 }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Box key={index} sx={{ mb: 1.5 }}>
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={36}
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            ))}
          </Box>
        )}

        {!isLoadingList && reports.length === 0 && !isGenerating && (
          <Box
            sx={{
              px: 2,
              py: 4,
              textAlign: "center",
            }}
          >
            <ReportIcon
              sx={{
                fontSize: 32,
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(0, 0, 0, 0.1)",
                mb: 1,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.4)"
                    : "rgba(0, 0, 0, 0.35)",
                fontSize: "0.75rem",
                lineHeight: 1.4,
                display: "block",
              }}
            >
              No reports yet
            </Typography>
          </Box>
        )}

        {/* Generating placeholder - optimistic update */}
        {isGenerating && (
          <Box
            sx={{
              mx: 1,
              my: 0.5,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(34, 197, 94, 0.08)"
                  : "rgba(34, 197, 94, 0.04)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={10} sx={{ color: "#22c55e" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "#22c55e",
                  fontSize: "0.7rem",
                  fontWeight: 500,
                }}
              >
                Generating...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Report items */}
        {!isLoadingList &&
          reports.map((report: ReportListItem, index: number) => {
            const isSelected = selectedReportId === report.id;
            const { date, year, time } = formatReportDate(report.generatedAt);
            const isLatest = index === 0;

            return (
              <Box
                key={report.id}
                onClick={() => onSelectReport(report.id)}
                sx={{
                  mx: 1,
                  my: 0.5,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  cursor: "pointer",
                  backgroundColor: isSelected
                    ? theme.palette.mode === "dark"
                      ? "rgba(34, 197, 94, 0.12)"
                      : "rgba(34, 197, 94, 0.08)"
                    : "transparent",
                  transition: "background-color 0.15s ease",
                  "&:hover": {
                    backgroundColor: isSelected
                      ? theme.palette.mode === "dark"
                        ? "rgba(34, 197, 94, 0.12)"
                        : "rgba(34, 197, 94, 0.08)"
                      : theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.04)"
                        : "rgba(0, 0, 0, 0.025)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: isSelected
                          ? "#22c55e"
                          : theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.85)"
                            : "rgba(0, 0, 0, 0.75)",
                        fontSize: "0.8rem",
                        fontWeight: isSelected ? 600 : 500,
                        lineHeight: 1.2,
                      }}
                    >
                      {date}, {year}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.4)"
                            : "rgba(0, 0, 0, 0.35)",
                        fontSize: "0.65rem",
                      }}
                    >
                      {time}
                    </Typography>
                  </Box>
                  {isLatest && (
                    <Box
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 0.5,
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(34, 197, 94, 0.15)"
                            : "rgba(34, 197, 94, 0.1)",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#22c55e",
                          fontSize: "0.55rem",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                        }}
                      >
                        New
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
      </Box>

      {/* Quota indicator at bottom */}
      <QuotaIndicator />
    </Box>
  );
};

export default ReportSidebar;
